// RemembranceDates.js — the rhythm of remembrance.
// Shows the next meaningful date (their birthday, or the anniversary of
// their passing) as a quiet countdown — and on the day itself, the page
// opens with "Today we remember", inviting a candle.

import React, { useMemo } from 'react';

const toDate = (d) => {
  if (!d) return null;
  const date = d.seconds ? new Date(d.seconds * 1000) : new Date(d);
  return Number.isNaN(date.getTime()) ? null : date;
};

const ordinal = (n) => {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

function nextOccurrence(date) {
  const now = new Date();
  const thisYear = new Date(now.getFullYear(), date.getMonth(), date.getDate());
  const target = thisYear >= new Date(now.getFullYear(), now.getMonth(), now.getDate())
    ? thisYear
    : new Date(now.getFullYear() + 1, date.getMonth(), date.getDate());
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const days = Math.round((target - today) / 86400000);
  const years = target.getFullYear() - date.getFullYear();
  return { target, days, years, isToday: days === 0 };
}

function RemembranceDates({ memorial }) {
  const occasions = useMemo(() => {
    const list = [];
    const birth = toDate(memorial?.birthDate);
    const death = toDate(memorial?.deathDate);
    const first = memorial?.firstName || 'them';

    if (birth) {
      const o = nextOccurrence(birth);
      list.push({
        ...o,
        key: 'birthday',
        label: `${first}'s ${ordinal(o.years)} birthday`,
        todayLabel: `today would be ${first}'s ${ordinal(o.years)} birthday`,
      });
    }
    if (death) {
      const o = nextOccurrence(death);
      list.push({
        ...o,
        key: 'anniversary',
        label: `${ordinal(o.years)} anniversary of remembrance`,
        todayLabel: `the ${ordinal(o.years)} anniversary of ${first}'s passing`,
      });
    }
    return list.sort((a, b) => a.days - b.days);
  }, [memorial]);

  if (occasions.length === 0) return null;
  const nearest = occasions[0];

  // ------- The day itself: a banner of remembrance -------
  if (nearest.isToday) {
    return (
      <div
        className="mb-8 rounded-2xl p-6 md:p-8 text-center relative overflow-hidden"
        style={{ background: 'radial-gradient(ellipse at 50% 0%, #1a1530 0%, #05040d 80%)' }}
      >
        <div
          className="absolute left-1/2 -translate-x-1/2 top-4 w-2 h-2 rounded-full pointer-events-none"
          style={{
            background: '#fff3d0',
            boxShadow: '0 0 18px 7px rgba(255,220,150,0.7), 0 0 60px 24px rgba(255,205,120,0.25)',
            animation: 'soulStarPulse 3.8s ease-in-out infinite',
          }}
        />
        <p className="mt-6 text-[10.5px] uppercase" style={{ letterSpacing: '0.34em', color: '#e5c883' }}>
          A Day of Remembrance
        </p>
        <h3 className="mt-3 text-xl md:text-2xl font-extralight text-white" style={{ letterSpacing: '0.05em' }}>
          Today we remember {memorial?.firstName} — {nearest.todayLabel}
        </h3>
        <div className="mt-5 flex items-center justify-center gap-3 flex-wrap">
          <a
            href="#candle-vigil"
            className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full text-[11px] uppercase transition-shadow hover:shadow-[0_0_24px_rgba(255,215,140,0.35)]"
            style={{
              letterSpacing: '0.22em',
              color: '#ffe2a8',
              border: '1px solid rgba(184,150,79,0.5)',
              background: 'rgba(255,255,255,0.05)',
              textDecoration: 'none',
            }}
          >
            🕯️ Light a candle in their honor
          </a>
          {memorial?.galaxyLocation?.starId && (
            <a
              href="#light-star"
              className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full text-[11px] uppercase transition-shadow hover:shadow-[0_0_24px_rgba(255,215,140,0.35)]"
              style={{
                letterSpacing: '0.22em',
                color: '#ffe9b8',
                border: '1px solid rgba(229,200,131,0.7)',
                background: 'rgba(229,200,131,0.1)',
                textDecoration: 'none',
              }}
            >
              ✦ Light their star tonight
            </a>
          )}
        </div>
      </div>
    );
  }

  // ------- Any other day: a quiet countdown -------
  return (
    <div className="mb-8 text-center">
      <p
        className="inline-flex items-center gap-2 text-[11px] uppercase text-gray-400"
        style={{ letterSpacing: '0.22em' }}
      >
        <span style={{ color: '#cbab6a' }}>✦</span>
        <span>
          {nearest.label} ·{' '}
          {nearest.target.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })} ·{' '}
          {nearest.days === 1 ? 'tomorrow' : `in ${nearest.days} days`}
        </span>
        <span style={{ color: '#cbab6a' }}>✦</span>
      </p>
    </div>
  );
}

export default RemembranceDates;
